import type { ElementType } from 'react';
import styled, { css, keyframes } from 'styled-components';

const fadeIn = keyframes({
  from: {
    opacity: 0,
  },

  to: {
    opacity: 1,
  },
});

interface withFadeInProps {
  $show?: boolean;
}

export const withFadeInAnimation = (Component: ElementType) =>
  styled(Component)<withFadeInProps>(({ $show }) =>
    $show
      ? css`
          animation: ${fadeIn} 0.3s ease-in-out 0.3s both;
        `
      : undefined,
  );

const slideInUpFadeIn = keyframes({
  from: {
    opacity: 0,
    transform: 'translateY(-50px)',
  },

  to: {
    opacity: 1,
    transform: 'translateY(0)',
  },
});

interface withSlideInUpFadeInProps {
  $show?: boolean;
  $isMobile?: boolean;
}

export const withSlideInUpFadeIn = (Component: ElementType) =>
  styled(Component)<withSlideInUpFadeInProps>(({ $show, $isMobile }) =>
    $show
      ? css`
          animation: ${$isMobile ? fadeIn : slideInUpFadeIn} 0.3s ease-in-out;
        `
      : undefined,
  );

const fadeInScaleIn = ({
  initial,
  end,
}: {
  initial: {
    top: number;
    left: number;
    width: number;
    height: number;
  };
  end: {
    top: number;
    left: number;
    width: number;
    height: number;
  };
}) =>
  keyframes({
    from: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      opacity: 0,
      width: initial.width,
      height: initial.height,
      transform: 'translate(-50%, -50%) scale(0)',
    },

    '16.66%, 83.33%': {
      position: 'absolute',
      top: '50%',
      left: '50%',
      opacity: 1,
      width: initial.width,
      height: initial.height,
      transform: 'translate(-50%, -50%) scale(1)',
    },

    to: {
      position: 'absolute',
      top: end.top,
      left: end.left,
      width: end.width,
      height: end.height,
      opacity: 1,
      transform: 'translate(0%, 0%) scale(1)',
    },
  });

interface withFadeInScaleInAnimationProps {
  $hasAnimation?: boolean;
  $initialState: {
    top: number;
    left: number;
    width: number;
    height: number;
  };
  $finalState: {
    top: number;
    left: number;
    width: number;
    height: number;
  };
}

export const withFadeInScaleInAnimation = (Component: ElementType) =>
  styled(Component)<withFadeInScaleInAnimationProps>(
    ({ $hasAnimation, $initialState, $finalState }) =>
      $hasAnimation
        ? css`
            animation: ${fadeInScaleIn({
              initial: $initialState,
              end: $finalState,
            })}
              6s ease-in-out;
          `
        : undefined,
  );

const slideInRightFadeIn = keyframes({
  from: {
    opacity: 0,
    transform: 'translateX(50px)',
  },

  to: {
    opacity: 1,
    transform: 'translateX(0)',
  },
});

interface withSlideInRightFadeInProps {
  $show?: boolean;
  $isMobile?: boolean;
}

export const withSlideInRightFadeIn = (Component: ElementType) =>
  styled(Component)<withSlideInRightFadeInProps>(({ $show, $isMobile }) =>
    $show
      ? css`
          animation: ${$isMobile ? fadeIn : slideInRightFadeIn} 0.3s ease-in-out 0.15s
            both;
        `
      : undefined,
  );
